import React, { memo, useCallback, useContext, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  FitViewOptions,
  MiniMap,
  Node as ReactFlowNode,
  useReactFlow,
  ViewportHelperFunctionOptions,
} from 'reactflow';
import { twJoin } from 'tailwind-merge';

import { RyeconRerun } from '@dbt-labs/sourdough';

import { EDGE_TYPES, NODE_TYPES, NodeType } from '../dag/dagConstants';
import { convertFromReactFlowToDagNode, isMatchingNode } from '../dag/internalDag';
import { ValidatedDagProps } from '../dag/InternalDagInterfaces';
import { nodeColor } from '../dag/resourceConstants';
import { COLORS, getEdgeColor } from '../dag/styles';
import { DagProps, DbtDagNode } from '../dbt-dag';
import { OnNodeClick, useEventQueue } from '../hooks/useEventQueue';
import { useSyncNodesToFit } from '../hooks/useSyncNodesToFit';
import { useIsZoomLevelHalf, useIsZoomLevelQuarter } from '../hooks/useZoomLevel';
import { useContextMenuProvider } from '../providers/ContextMenuProvider';
import { DagContext } from '../providers/DagProvider';
import { useInternalDagContext } from '../providers/InternalDagProvider';
import { useViewportContext } from '../providers/ViewportProvider';
import { useDagreDagLayout } from '../utils/dagre';
import { useRenderGraph } from '../utils/render';
import { DagStyles } from './DagStyles';
import { DagToolbar } from './DagToolbar';
import { proOptions } from './reactFlowProOptions';
import { Subcomponents } from './Subcomponents';

export const ValidatedDag = <TDbtDagNode extends DbtDagNode>({
  validatedDagMap,
  primaryNodeIds,
  onNodeInteraction,
  forceRenderComplexDag,
  onDagLayoutStart,
  onDagLayoutResult,
  onDagRenderStart,
  onRenderComplete,
  zoomMode,
  showResetButton = true,
  scrollToZoom = true,
  enableMinimap = false,
  children,
}: ValidatedDagProps<TDbtDagNode>) => {
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const dagContainerRef = React.useRef<HTMLDivElement>(null);

  const { toolbarItems } = useInternalDagContext();

  // Retrieve from Dagre, or dagre cache
  const { dagreResult, dagGraph } = useDagreDagLayout(
    validatedDagMap,
    forceRenderComplexDag,
    onDagLayoutStart,
    onDagLayoutResult,
  );

  const viewport = useViewportContext();
  const reactFlow = useReactFlow();

  // Update nodes and edges in ReactFlow to render
  const renderedGraph = useRenderGraph({
    dagContainerEl: dagContainerRef.current,
    dagGraph,
    dagreResult,
    primaryNodeIds,
    forceRenderComplexDag,
    onDagRenderStart,
    onRenderComplete,
  });

  const onNodeClick: OnNodeClick<TDbtDagNode> = (_event, node) => {
    if (!isMountedRef.current) return;
    viewport.onInteraction();
    if (
      renderedGraph.highlightedNodeIds?.length !== 1 ||
      renderedGraph.highlightedNodeIds[0] !== node.id
    ) {
      dagContext.updateHighlightedNode([node.id]);
    }
    onNodeInteraction?.({
      interactionType: 'single_click',
      targetNode: node,
    });
  };

  const onNodeDoubleClick: OnNodeClick<TDbtDagNode> = (_event, node) => {
    viewport.onInteraction();
    if (
      primaryNodeIds &&
      primaryNodeIds.length === 0 &&
      isMatchingNode(node, primaryNodeIds[0])
    ) {
      // Click on the primary node
      return;
    }
    if (!node.id) return;
    onNodeInteraction?.({
      interactionType: 'double_click',
      targetNode: node,
    });
  };

  const contextMenu = useContextMenuProvider();

  const dagContext = useContext(DagContext);

  const resetDagLayoutAndZoom = () => {
    renderedGraph.resetNodes();
    viewport.resetView();
  };

  useEventQueue({
    resetDagLayoutAndZoom,
    onNodeClick,
    onNodeDoubleClick,
    updateHighlightedNodes: renderedGraph.updateHighlightedNode,
    onNodeRightClick: contextMenu.onNodeContextMenu,
    hideContextMenu: contextMenu.hideContextMenu,
    zoomIn: (options?: ViewportHelperFunctionOptions) => reactFlow.zoomIn(options),
    zoomOut: (options?: ViewportHelperFunctionOptions) => reactFlow.zoomOut(options),
    fitView: (options?: FitViewOptions) => reactFlow.fitView(options),
  });

  useSyncNodesToFit({
    zoomMode,
    primaryNodeIds,
    dagGraph,
  });

  const isZoomLevelQuarter = useIsZoomLevelQuarter();
  const isZoomLevelHalf = useIsZoomLevelHalf();

  return (
    <div
      ref={dagContainerRef}
      className={twJoin(
        'relative h-full w-full bg-bgNeutralMuted',
        isZoomLevelQuarter
          ? 'zoom-level-quarter'
          : isZoomLevelHalf
            ? 'zoom-level-half'
            : undefined,
      )}
    >
      <DisplayedDag
        renderedGraph={renderedGraph}
        scrollToZoom={scrollToZoom}
        enableMinimap={enableMinimap}
        primaryNodeIds={primaryNodeIds}
        onNodeInteraction={onNodeInteraction}
      />
      <renderedGraph.LiveRegion />
      <div className="pointer-events-none absolute top-0 z-10 h-full w-full">
        <DagToolbar
          toolbarItems={[
            ...(toolbarItems ?? []),
            ...(showResetButton
              ? [
                  {
                    tooltip: 'Reset',
                    ryecon: RyeconRerun,
                    action: () => {
                      resetDagLayoutAndZoom();
                    },
                  },
                ]
              : []),
          ]}
        />
        <Subcomponents>{children}</Subcomponents>
        <contextMenu.DagContextMenu
          dagContainerEl={dagContainerRef.current}
          boundingBox={renderedGraph.boundingBox}
        />
      </div>
    </div>
  );
};

type DisplayedDagProps = {
  renderedGraph: ReturnType<typeof useRenderGraph<any>>;
  scrollToZoom: boolean;
  enableMinimap: boolean;
  primaryNodeIds: string[] | undefined;
  onNodeInteraction: DagProps<any>['onNodeInteraction'];
};

const DisplayedDag = memo(
  ({
    renderedGraph,
    scrollToZoom,
    enableMinimap,
    primaryNodeIds,
    onNodeInteraction,
  }: DisplayedDagProps) => {
    const displayedDagRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const viewport = useViewportContext();
    const dagContext = useContext(DagContext);

    const { zoomIn, zoomOut, getViewport } = useReactFlow();

    // Use a ref to break the dependency cycle:
    // onMoveEnd → updateVisibleEdges → setEdges → renderedGraph → onMoveEnd
    const updateVisibleEdgesRef = useRef(renderedGraph.updateVisibleEdges);
    updateVisibleEdgesRef.current = renderedGraph.updateVisibleEdges;

    const onMoveEnd = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;
      const { width, height } = container.getBoundingClientRect();
      const currentViewport = getViewport();
      updateVisibleEdgesRef.current(currentViewport, width, height);
    }, [getViewport]);

    // Re-filter edges on initial render and when nodes change (debounced)
    const { nodes: renderedNodes } = renderedGraph.reactFlowProps;
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          onMoveEnd();
        });
      }, 150);
      return () => clearTimeout(timeoutId);
    }, [onMoveEnd, renderedNodes]);

    const { onNodeRightClick } = dagContext;
    const onNodeContextMenu = useCallback(
      (
        e: React.MouseEvent<Element, MouseEvent>,
        reactNode: ReactFlowNode<any, string | undefined>,
      ) => {
        return onNodeRightClick(
          e,
          convertFromReactFlowToDagNode(
            reactNode as ReactFlowNode<any, NodeType | undefined>,
          ),
        );
      },
      [onNodeRightClick],
    );

    const onWheel = useCallback(
      (event: WheelEvent) => {
        if (!event.metaKey) return;
        event.preventDefault();
        const delta = event.deltaY;
        if (delta > 0) {
          zoomOut();
          return;
        }
        zoomIn();
      },
      [zoomIn, zoomOut],
    );

    useEffect(() => {
      const displayedDag = displayedDagRef.current;
      if (displayedDag) {
        displayedDag.addEventListener('wheel', onWheel, { passive: false });
        return () => {
          displayedDag.removeEventListener('wheel', onWheel);
        };
      }
    }, [onWheel]);

    return (
      <>
        <DagStyles />
        <ReactFlow
          ref={(el) => {
            displayedDagRef.current = el;
            containerRef.current = el;
          }}
          proOptions={proOptions}
          edgesFocusable={false}
          {...renderedGraph.reactFlowProps}
          onClick={(e: any) => {
            const classList: string | undefined = e.target?.classList?.value;
            if (classList?.split(' ')?.includes('react-flow__pane')) {
              dagContext.clearSelected?.();
              renderedGraph.updateHighlightedNode(undefined);
              onNodeInteraction?.({
                interactionType: 'clear_selection',
                targetNode: null,
              });
            }
          }}
          onMoveStart={viewport.onInteraction}
          onMoveEnd={onMoveEnd}
          fitView
          zoomOnScroll={scrollToZoom}
          preventScrolling={scrollToZoom}
          fitViewOptions={viewport.fitViewOptions}
          minZoom={viewport.minZoom}
          maxZoom={viewport.maxZoom}
          onlyRenderVisibleElements={true}
          elementsSelectable
          defaultMarkerColor={getEdgeColor({
            isHighlighted: (renderedGraph.highlightedNodeIds?.length ?? 0) > 0,
            isError: false,
          })}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          onKeyUp={(e) => {
            if (e.code === 'Escape') {
              renderedGraph.updateHighlightedNode(primaryNodeIds);
            }
          }}
          multiSelectionKeyCode={null}
          zoomActivationKeyCode={undefined}
          onNodeContextMenu={onNodeContextMenu}
        >
          <Background color={COLORS.BG_DOTS} gap={18} size={1} />
          {enableMinimap && (
            <MiniMap position="bottom-left" nodeColor={nodeColor} zoomable pannable />
          )}
        </ReactFlow>
      </>
    );
  },
);
